import React, { forwardRef, useEffect, useState } from 'react'
import _ from 'lodash'
import clsx from 'clsx'
import countDecimals from '../../utils/countDecimals'
import Icon from '../Icon'


const ControlledQuantitySelector = forwardRef(function ControlledQuantitySelector(props, ref) {

	const {
        className,
		inputRef,
        value,
        onChange,
        min,
        max,
        step,
        debouncingTime,
        disabled,
        format,
        ...other
	} = props

    const [textValue, setTextValue] = useState(value)
    // While the input has focus it always shows the raw number, so it stays typeable; the
    // `format` output (e.g. "62 1/4") is display-only — see the prop docs in QuantitySelector.
    const [isFocused, setIsFocused] = useState(false)

    const onChangeDebounced = _.debounce((valueToEmit) => {
        if (Number(valueToEmit) < min) {
            setTextValue(`${min}`)
            return
        }
        onChange(valueToEmit)
    }, debouncingTime)

    useEffect(() => {
        setTextValue(`${value}`)
    }, [value])

    useEffect(() => {
        onChangeDebounced(Number(textValue))
        return onChangeDebounced.cancel
    }, [textValue])

    const handleFocus = (e) => {
        setIsFocused(true)
        const input = e.target
        // The displayed text changes on focus when `format` is set (formatted → raw), so select
        // after that re-render or the selection would land on the text being replaced.
        format ? requestAnimationFrame(() => input.select()) : input.select()
    }

    const handleBlur = () => setIsFocused(false)

    const displayValue = format && !isFocused && `${textValue}` !== ''
        ? format(Number(textValue))
        : textValue

    const handleChange = (inputValue) => {
        onChangeDebounced.cancel()
        inputValue = inputValue.replace(',', '.')
        if (inputValue === '') {
            setTextValue(inputValue)
            return
        }

        if (Number(inputValue) > max) {
            inputValue = `${max}`
        }
        setTextValue(inputValue)
    }

    const handleClickSusbstract = () => {
        const newValue = (Number(value) - step).toFixed(countDecimals(step))
        if (newValue < min) {
            setTextValue(min)
            onChange(min)
            return
        }

        setTextValue(newValue)
        onChange(newValue)
    }

    const handleClickAdd = () => {
        const newValue = (Number(value) + step).toFixed(countDecimals(step))
        if (newValue > max) {
            setTextValue(min)
            onChange(max)
            return
        }

        setTextValue(newValue)
        onChange(newValue)
    }

	return (
		<div ref={ref} className={clsx('Vb-qty-selector', className)} {...other}>
            <button
                className="Vb-qty-selector__btn-substract"
                onClick={handleClickSusbstract}
                disabled={disabled || Number(textValue) <= min}
            >
				<Icon size={0.75}>minus</Icon>
			</button>
			<input
				ref={inputRef}
				type="text"
                value={displayValue}
                onChange={(e) => {
                    handleChange(e.target.value)
                }}
                disabled={disabled}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={(e) => {
                    // Allways allow deletion
					if (['Delete', 'Backspace'].includes(e.key)) {
						return
					}

                    // Restrict more than one decimal separator
					if (/[,.]/.test(e.key) && /[,.]/.test(e.target.value)) {
						e.preventDefault()
						return
					}

                    // Restrict decimal separator at the beggining
					if (/[,.]/.test(e.key) && !`${value}`.length) {
						e.preventDefault()
						return
					}

                    // Restrict more decimals than allowed by step prop
					if (countDecimals(step) && countDecimals(+e.target.value.replace(',', '.')) >= countDecimals(step) && e.target.selectionStart === e.target.selectionEnd) {
						e.preventDefault()
						return
					}
                }}
			/>
            <button
                className="Vb-qty-selector__btn-add"
                onClick={handleClickAdd}
                disabled={disabled || Number(textValue) >= max}
            >
				<Icon size={0.75}>plus</Icon>
			</button>
		</div>
	)

})

export default ControlledQuantitySelector